import React, { useEffect, useMemo, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Dialog, DialogTitle, DialogBody } from './dialog';
import { Button } from './button';
import { Text } from './text';
import { useUser } from './UserContext';
import { useOrgMiembros } from '../hooks/useOrgMiembros';
import { logBitacora } from '../utils/bitacora';
import { PENDIENTE_TIPO_LABELS, normalizarAsignados } from '../utils/pendientes';

const MESSAGE_MAX_LEN = 500;
const field =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none ' +
  'focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia';

/**
 * Remind teammates of one pendiente by email — the internal counterpart of
 * PendienteEmailDialog. Recipients are org members only: the asignados come
 * preselected (minus yourself), everyone else is a checkbox away. The
 * recordarPendiente callable re-checks membership and consent.
 */
export function PendienteRecordarDialog({ open, onClose, pendiente }) {
  const { user, userData } = useUser();
  const { miembros } = useOrgMiembros();
  const [elegidos, setElegidos] = useState(() => new Set());
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [resultado, setResultado] = useState(null);

  const asignados = useMemo(() => normalizarAsignados(pendiente || {}), [pendiente]);

  useEffect(() => {
    if (!open) return;
    setElegidos(new Set(asignados.filter((u) => u !== user?.uid)));
    setMessage('');
    setError(null);
    setResultado(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendiente?.id]);

  // Asignados first, then the rest of the team.
  const lista = useMemo(() => {
    const conCorreo = miembros.filter((m) => m.correo);
    return [
      ...conCorreo.filter((m) => asignados.includes(m.id)),
      ...conCorreo.filter((m) => !asignados.includes(m.id)),
    ];
  }, [miembros, asignados]);

  const toggle = (uid) => {
    setElegidos((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const handleSend = async () => {
    if (!pendiente || elegidos.size === 0) return;
    setSending(true);
    setError(null);
    try {
      const recordar = httpsCallable(getFunctions(), 'recordarPendiente');
      const res = await recordar({
        pendienteId: pendiente.id,
        uids: [...elegidos],
        message: message.trim(),
      });
      const sent = res.data?.sent || [];
      const omitidos = res.data?.omitidos || [];
      const documentId = pendiente.convenioId || pendiente.clienteId;
      if (documentId) {
        try {
          await logBitacora({
            action: 'UPDATE',
            collection: pendiente.convenioId ? 'convenios' : 'clientes',
            documentId,
            user,
            userData,
            metadata: {
              description: `Recordó por correo a ${sent.join(', ')}: ${pendiente.titulo}`,
              clientId: pendiente.clienteId || null,
              convenioId: pendiente.convenioId || null,
              contratoId: pendiente.contratoId || null,
              pendienteIds: [pendiente.id],
            },
          });
        } catch (logError) {
          console.error('Failed to log pendiente reminder:', logError);
        }
      }
      if (omitidos.length > 0) {
        setResultado({ sent, omitidos });
      } else {
        onClose();
      }
    } catch (err) {
      console.error('Error sending pendiente reminder:', err);
      setError(err?.message || 'No se pudo enviar el correo.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog size="md" open={open} onClose={sending ? () => {} : onClose}>
      <DialogTitle>
        <span className="text-azul-surelia">Recordar al responsable</span>
      </DialogTitle>
      <DialogBody>
        {resultado ? (
          <div className="space-y-4">
            <Text className="text-sm text-zinc-700">
              Enviado a {resultado.sent.join(', ')}.
            </Text>
            <Text className="text-sm text-zinc-600">
              No reciben correos de pendientes: {resultado.omitidos.join(', ')}.
            </Text>
            <div className="flex justify-end">
              <Button color="surelia" onClick={onClose}>Cerrar</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {pendiente && (
              <div className="rounded-lg border border-azul-surelia/15 px-3 py-2">
                <Text className="text-sm font-medium text-zinc-800">{pendiente.titulo}</Text>
                <Text className="text-xs text-zinc-500">
                  {PENDIENTE_TIPO_LABELS[pendiente.tipo] || pendiente.tipo}
                </Text>
              </div>
            )}

            {lista.length === 0 ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-azul-surelia"></div>
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto border border-azul-surelia/15 rounded-lg divide-y divide-azul-surelia/10">
                {lista.map((m) => (
                  <label
                    key={m.id}
                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={elegidos.has(m.id)}
                      onChange={() => toggle(m.id)}
                      className="rounded border-gray-300 text-azul-surelia focus:ring-azul-surelia"
                    />
                    <div className="min-w-0 flex-1">
                      <Text className="text-sm text-zinc-800 truncate">
                        {m.nombre || m.correo}
                        {m.id === user?.uid ? ' (tú)' : ''}
                      </Text>
                      <Text className="text-xs text-zinc-500 truncate">
                        {m.nombre ? m.correo : ''}
                        {asignados.includes(m.id) ? `${m.nombre ? ' — ' : ''}Asignado` : ''}
                      </Text>
                    </div>
                  </label>
                ))}
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Mensaje (opcional)
              </label>
              <textarea
                rows={3}
                value={message}
                maxLength={MESSAGE_MAX_LEN}
                onChange={(e) => setMessage(e.target.value)}
                className={field}
              />
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button plain onClick={onClose} disabled={sending}>Cancelar</Button>
              <Button
                color="surelia"
                onClick={handleSend}
                disabled={sending || elegidos.size === 0}
              >
                {sending ? 'Enviando…' : 'Enviar'}
              </Button>
            </div>
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
}

export default PendienteRecordarDialog;
